import { useState, useEffect } from "react";
import { useApp } from "@/context/AppContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Eye, Plus, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ObservationsTab() {
  const { child, setChild } = useApp();
  const [observations, setObservations] = useState([]);
  const [customObs, setCustomObs] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!child) return;
    // Try from local state first, then fetch from backend
    const localObs = [...(child.observation_points || []), ...(child.custom_observations || [])];
    if (localObs.length > 0) {
      setObservations(localObs.map((o) => ({ title: o, enabled: true })));
    } else if (child.id) {
      // Fetch from backend
      axios.get(`${API}/children/${child.id}`).then((res) => {
        const data = res.data;
        const allObs = [...(data.observation_points || []), ...(data.custom_observations || [])];
        setObservations(allObs.map((o) => ({ title: o, enabled: true })));
        // Update local child state
        setChild((prev) => prev ? { ...prev, observation_points: data.observation_points, custom_observations: data.custom_observations } : prev);
      }).catch(() => {});
    }
  }, [child, setChild]);

  const toggleObs = (index) => {
    setObservations((prev) => prev.map((o, i) => i === index ? { ...o, enabled: !o.enabled } : o));
  };

  const addCustom = async () => {
    if (!customObs.trim() || !child) return;
    const newObs = customObs.trim();
    const updatedCustom = [...(child.custom_observations || []), newObs];
    const updatedPoints = [...(child.observation_points || []), newObs];
    try {
      const res = await axios.put(`${API}/children/${child.id}`, {
        observation_points: updatedPoints,
        custom_observations: updatedCustom,
      });
      setChild(res.data);
      setCustomObs("");
      toast.success("Observation point added");
    } catch { toast.error("Could not add observation"); }
  };

  const saveChanges = async () => {
    if (!child) return;
    setLoading(true);
    const enabled = observations.filter((o) => o.enabled).map((o) => o.title);
    try {
      const res = await axios.put(`${API}/children/${child.id}`, { observation_points: enabled });
      setChild(res.data);
      toast.success("Observations updated");
    } catch { toast.error("Could not update"); }
    setLoading(false);
  };

  return (
    <div className="p-4 pt-6" data-testid="observations-tab">
      <div className="flex items-center gap-2 mb-6">
        <Eye size={24} className="text-[#8E9B82]" strokeWidth={1.5} />
        <h1 className="font-outfit text-3xl font-medium text-[#2F3630] tracking-tight">Observations</h1>
      </div>
      <p className="font-manrope text-sm text-[#6B7264] mb-6">
        Manage what Aavaa tracks for {child?.name || "your child"}. Toggle points on or off and add custom ones.
      </p>

      {/* Observation List */}
      <div className="space-y-2 mb-6">
        {observations.map((obs, i) => (
          <div
            key={i}
            className={`flex items-center justify-between px-4 py-3 rounded-xl border transition-all ${
              obs.enabled ? "bg-white border-[#E2DFD8]" : "bg-[#F7F5F0] border-[#E2DFD8]/50 opacity-60"
            }`}
            data-testid={`obs-item-${i}`}
          >
            <span className={`font-manrope text-sm flex-1 mr-3 ${obs.enabled ? "text-[#2F3630]" : "text-[#6B7264] line-through"}`}>
              {obs.title}
            </span>
            <Switch
              checked={obs.enabled}
              onCheckedChange={() => toggleObs(i)}
              data-testid={`obs-switch-${i}`}
              className="data-[state=checked]:bg-[#8E9B82]"
            />
          </div>
        ))}
      </div>

      {/* Add Custom */}
      <div className="bg-white rounded-[24px] p-5 border border-[#E2DFD8]/50 mb-6">
        <h3 className="font-outfit text-base font-medium text-[#2F3630] mb-3">Add custom observation</h3>
        <div className="flex gap-2">
          <Input
            data-testid="add-obs-input"
            value={customObs}
            onChange={(e) => setCustomObs(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addCustom()}
            placeholder="What specific thing should Aavaa notice?"
            className="rounded-xl border-[#E2DFD8] h-10 text-sm flex-1 focus:ring-2 focus:ring-[#D4A373]/20 focus:border-[#D4A373]"
          />
          <Button
            data-testid="add-obs-btn"
            onClick={addCustom}
            disabled={!customObs.trim()}
            variant="outline"
            className="rounded-xl h-10 px-3 border-[#D4A373] text-[#D4A373] hover:bg-[#D4A373]/10"
          >
            <Plus size={16} />
          </Button>
        </div>
      </div>

      {/* Save */}
      <Button
        data-testid="save-observations-btn"
        onClick={saveChanges}
        disabled={loading}
        className="w-full rounded-full bg-[#8E9B82] hover:bg-[#7A8A6E] text-white h-12 font-outfit font-medium transition-all duration-300 hover:scale-[0.98] active:scale-95"
      >
        {loading ? "Saving..." : "Save Changes"}
      </Button>
    </div>
  );
}
